import { z } from 'zod';
import { ClassIdSchema, DBClassClassIdSchema, DBClassSubjectSchema } from '../classes/schemas';
import {
  BILLING_TYPE_LABELS,
  BillingType,
  ENROLMENT_METHOD_LABELS,
  EnrolmentMethod,
} from '../common/enums';
import { enumLabels } from '../common/fields';
import { StringBooleanSchema, UnixTimestampSchema } from '../common/schemas';
import { DBCourseCourseIdSchema } from '../courses/schemas';
import { DBLessonCounterSchema, DBLessonStreamSchema, LessonIdSchema } from '../lessons/schemas';
import {
  DBLessonTemplateCourseSchema,
  DBLessonTemplateLessonIdSchema,
} from '../lessonTemplates/schemas';
import { AttendanceType } from '../students/enums';
import { DBStudentAttendanceTypeSchema, DBStudentStudentIdSchema } from '../students/schemas';

/**
 * Shared & Helper Schemas
 */

/**
 * DB Schemas
 */

export const DBLessonEnrolmentStudentSchema = z.lazy(() => DBStudentStudentIdSchema);
export const DBLessonEnrolmentClassLessonIdentifierSchema = z
  .string()
  .refine(val => {
    const parts = val.split('#');
    return (
      parts.length === 5 &&
      z.lazy(() => DBClassSubjectSchema).safeParse(parts[0]).success &&
      z.lazy(() => DBClassClassIdSchema).safeParse(parts[1]).success &&
      z.lazy(() => DBLessonTemplateCourseSchema).safeParse(parts[2]).success &&
      z.lazy(() => DBLessonTemplateLessonIdSchema).safeParse(parts[3]).success &&
      z.lazy(() => DBLessonCounterSchema).safeParse(parts[4]).success
    );
  }, 'Must be in format subject#classId#course#lessonId#counter')
  .openapi({
    description:
      'A composite key combining subject, class ID, course, lesson ID, and counter, formatted as "subject#classId#course#lessonId#counter"',
    example: 'chem#lychee#chem12#HPR1#0001',
  });
export const DBLessonEnrolmentAttendanceNotifiedSchema = z.boolean();
export const DBLessonEnrolmentAttendanceTypeSchema = z.lazy(() => DBStudentAttendanceTypeSchema);
export const DBLessonEnrolmentBillingTypeSchema = z
  .enum(BillingType)
  .register(enumLabels, { labels: BILLING_TYPE_LABELS });
export const DBLessonEnrolmentCourseSchema = z.lazy(() => DBCourseCourseIdSchema);
export const DBLessonEnrolmentEnrolmentMethodSchema = z
  .enum(EnrolmentMethod)
  .register(enumLabels, { labels: ENROLMENT_METHOD_LABELS });
export const DBLessonEnrolmentHomeworkSchema = z.object({
  complete: z.boolean().optional(),
});
export const DBLessonEnrolmentPresentSchema = z.boolean();
export const DBLessonEnrolmentRollMarkedSchema = StringBooleanSchema;
export const DBLessonEnrolmentSeparatelyBilledSchema = z.boolean();
export const DBLessonEnrolmentStartDatetimeSchema = UnixTimestampSchema;
export const DBLessonEnrolmentStreamSchema = z.lazy(() => DBLessonStreamSchema);

export const DBLessonEnrolmentSchema = z.object({
  student: DBLessonEnrolmentStudentSchema,
  classLessonIdentifier: DBLessonEnrolmentClassLessonIdentifierSchema,
  attendanceNotified: DBLessonEnrolmentAttendanceNotifiedSchema,
  attendanceType: DBLessonEnrolmentAttendanceTypeSchema,
  billingType: DBLessonEnrolmentBillingTypeSchema,
  course: DBLessonEnrolmentCourseSchema,
  enrolmentMethod: DBLessonEnrolmentEnrolmentMethodSchema,
  homework: DBLessonEnrolmentHomeworkSchema,
  present: DBLessonEnrolmentPresentSchema,
  rollMarked: DBLessonEnrolmentRollMarkedSchema,
  separatelyBilled: DBLessonEnrolmentSeparatelyBilledSchema,
  startDatetime: DBLessonEnrolmentStartDatetimeSchema,
  stream: DBLessonEnrolmentStreamSchema.optional(),
});

/**
 * Client Schemas
 */

export const LessonEnrolmentIdSchema = z.string().brand<'LessonEnrolmentId'>();
export const LessonEnrolmentStudentIdSchema = DBLessonEnrolmentStudentSchema;
export const LessonEnrolmentLessonIdSchema = z.lazy(() => LessonIdSchema);
export const LessonEnrolmentClassIdSchema = z.lazy(() => ClassIdSchema);
export const LessonEnrolmentSubjectSchema = z.lazy(() => DBClassSubjectSchema);
export const LessonEnrolmentClassCodeSchema = z.lazy(() => DBClassClassIdSchema);
export const LessonEnrolmentCourseIdSchema = DBLessonEnrolmentCourseSchema;
export const LessonEnrolmentLessonTemplateCodeSchema = z.lazy(() => DBLessonTemplateLessonIdSchema);
export const LessonEnrolmentCounterSchema = z.lazy(() => DBLessonCounterSchema);
export const LessonEnrolmentClassLabelSchema = z.string();
export const LessonEnrolmentAttendanceNotifiedSchema = DBLessonEnrolmentAttendanceNotifiedSchema;
export const LessonEnrolmentAttendanceTypeSchema = DBLessonEnrolmentAttendanceTypeSchema;
export const LessonEnrolmentBillingTypeSchema = DBLessonEnrolmentBillingTypeSchema;
export const LessonEnrolmentEnrolmentMethodSchema = DBLessonEnrolmentEnrolmentMethodSchema;
export const LessonEnrolmentHomeworkSchema = DBLessonEnrolmentHomeworkSchema;
export const LessonEnrolmentPresentSchema = DBLessonEnrolmentPresentSchema;
export const LessonEnrolmentRollMarkedSchema = z.boolean();
export const LessonEnrolmentSeparatelyBilledSchema = DBLessonEnrolmentSeparatelyBilledSchema;
export const LessonEnrolmentStartDatetimeSchema = DBLessonEnrolmentStartDatetimeSchema;
export const LessonEnrolmentStreamIdSchema = DBLessonEnrolmentStreamSchema;

export const LessonEnrolmentSchema = z.object({
  id: LessonEnrolmentIdSchema,
  studentId: LessonEnrolmentStudentIdSchema.meta({ title: 'Student' }),
  lessonId: LessonEnrolmentLessonIdSchema,
  classId: LessonEnrolmentClassIdSchema,
  subject: LessonEnrolmentSubjectSchema.meta({ title: 'Subject' }),
  classCode: LessonEnrolmentClassCodeSchema.meta({ title: 'Class code' }),
  courseId: LessonEnrolmentCourseIdSchema.meta({ title: 'Course' }),
  lessonTemplateCode: LessonEnrolmentLessonTemplateCodeSchema.meta({ title: 'Lesson' }),
  counter: LessonEnrolmentCounterSchema,
  // `cl-{courseId}-{classCode}`, from the lesson's own course
  classLabel: LessonEnrolmentClassLabelSchema,
  attendanceNotified: LessonEnrolmentAttendanceNotifiedSchema.default(false).meta({
    title: 'Attendance notified',
  }),
  attendanceType: LessonEnrolmentAttendanceTypeSchema.default(AttendanceType.InPerson).meta({
    title: 'Attendance type',
  }),
  billingType: LessonEnrolmentBillingTypeSchema.default(BillingType.Term),
  enrolmentMethod: LessonEnrolmentEnrolmentMethodSchema.default(EnrolmentMethod.Class).meta({
    title: 'Enrolment method',
  }),
  homework: LessonEnrolmentHomeworkSchema.default({}).meta({ title: 'Homework' }),
  present: LessonEnrolmentPresentSchema.default(false).meta({ title: 'Present' }),
  rollMarked: LessonEnrolmentRollMarkedSchema.default(false),
  separatelyBilled: LessonEnrolmentSeparatelyBilledSchema.default(false).meta({
    title: 'Separately billed',
  }),
  startDatetime: LessonEnrolmentStartDatetimeSchema.meta({ title: 'Start datetime' }),
  streamId: LessonEnrolmentStreamIdSchema.optional().meta({ title: 'Stream' }),
});
